/**
 * SatisfactionCell — the one manually-entered column.
 *
 * Layout, top to bottom: the rating, then the previous value beneath it, which
 * opens the full history. Notes live in the trailing note column, not in here —
 * a paragraph has no place in a dense grid.
 *
 * The scale is inverted: "Very disappointed" (if Hourglass went away) is good,
 * "Happy" is bad. Colour is binary — the two disappointed values green, the
 * other two red — because a four-point forced choice has no meaningful middle.
 *
 * Ratings are chosen from a picker rather than typed. They are a closed set of
 * phrases, and free text would invite near-misses that no longer match the
 * value the rest of the page compares against.
 */

import { useEffect, useRef, useState } from "react";
import { cn } from "@/shared/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import {
	SATISFACTION_HINTS,
	SATISFACTION_SCORES,
	isSatisfactionScore,
	satisfactionClass,
	type SatisfactionPair,
	type SatisfactionScore,
} from "../useSatisfaction";

/** `createdAt` is a full ISO timestamp; only the date is meaningful here. */
function formatDate(iso: string): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
	return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

interface SatisfactionCellProps {
	pair: SatisfactionPair | undefined;
	saving: boolean;
	onRecord: (score: SatisfactionScore) => void;
	onOpenHistory: () => void;
}

export function SatisfactionCell({
	pair,
	saving,
	onRecord,
	onOpenHistory,
}: SatisfactionCellProps) {
	const [editing, setEditing] = useState(false);
	const selectRef = useRef<HTMLSelectElement>(null);

	useEffect(() => {
		if (editing) selectRef.current?.focus();
	}, [editing]);

	// `current` is ranked over scored rows only, so a present `current` always
	// carries a rating. Absent means nobody has rated this timekeeper.
	const current = pair?.current ?? null;
	const previous = pair?.previous ?? null;

	function commit(value: string) {
		// Only a value from the closed set commits; anything else just closes the
		// editor rather than writing a rating nothing can interpret.
		if (isSatisfactionScore(value)) onRecord(value);
		setEditing(false);
	}

	return (
		<div className="flex flex-col items-end gap-0.5">
			{editing ? (
				<select
					ref={selectRef}
					defaultValue={current?.score ?? ""}
					onChange={(e) => commit(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Escape") setEditing(false);
					}}
					onBlur={() => setEditing(false)}
					aria-label="Satisfaction rating"
					className="rounded border bg-background px-1 py-0.5 text-xs outline-none ring-1 ring-ring"
				>
					{/* Shown when the stored value is not one this picker can represent:
					    no rating yet, or a value outside the closed set. Without it the
					    browser silently selects the first option, so the cell would
					    display a rating nobody recorded — and picking that same option
					    fires no change event, leaving the stale value unreplaceable.
					    It is NOT offered once a valid rating exists: the picker cannot
					    clear one, since an empty write is indistinguishable from a
					    note-only row and the server rejects a row carrying neither. */}
					{!isSatisfactionScore(current?.score ?? "") && (
						<option value="">Select…</option>
					)}
					{SATISFACTION_SCORES.map((score) => (
						<option key={score} value={score}>
							{score}
						</option>
					))}
				</select>
			) : (
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type="button"
							onClick={() => setEditing(true)}
							disabled={saving}
							aria-label={
								current
									? `Satisfaction: ${current.score}. Click to change.`
									: "No satisfaction rating. Click to add."
							}
							className={cn(
								"max-w-[11rem] truncate rounded px-1.5 py-0.5 text-xs font-medium",
								current
									? satisfactionClass(current.score)
									: "text-muted-foreground hover:bg-muted",
								saving && "opacity-50",
							)}
						>
							{current?.score ?? "—"}
						</button>
					</TooltipTrigger>
					<TooltipContent>
						{current
							? (SATISFACTION_HINTS[current.score as SatisfactionScore] ??
								current.score)
							: "Click to record a rating"}
					</TooltipContent>
				</Tooltip>
			)}

			{previous?.score ? (
				<button
					type="button"
					onClick={onOpenHistory}
					className="max-w-[11rem] truncate text-[10px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
				>
					{previous.score} on {formatDate(previous.createdAt)}
				</button>
			) : (
				<button
					type="button"
					onClick={onOpenHistory}
					disabled={!current}
					className="text-[10px] text-muted-foreground disabled:cursor-default"
				>
					n/a
				</button>
			)}
		</div>
	);
}
